import { FC, useCallback, useRef, useMemo } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { getRoute } from '@/routes/Routes.helpers';
import IcoBackChevron from '@/assets/svg/ico_back_chevron.svg?react';
import IcoClose from '@/assets/svg/ico_close.svg?react';
import Button from '@/components/controls/Button/Button.component';
import Tooltip from '@/components/elems/Tooltip/Tooltip.component';
import { ROUTES_WITH_TOOLTIP } from '@/components/widgets/HeaderWidget/Breadcrumbs/Breadcrumbs.config';
import styles from '@/components/widgets/HeaderWidget/Breadcrumbs/Breadcrumbs.module.scss';
import { BreadcrumbsPropsIF } from '@/components/widgets/HeaderWidget/Breadcrumbs/Breadcrumbs.types';

const Breadcrumbs: FC<BreadcrumbsPropsIF> = ({ backLinks }) => {
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const navigationType = useNavigationType();
    const containerRef = useRef<HTMLDivElement>(null);
    const textRef = useRef<HTMLSpanElement>(null);

    const navigateToArchive = useCallback((): void => {
        if (pathname !== backLinks.archiveRoute.path) {
            navigate(backLinks.archiveRoute.path);
        }
    }, [backLinks.archiveRoute.path, pathname, navigate]);

    const handleBack = useCallback((): void => {
        if (navigationType === 'POP') {
            navigate(backLinks.archiveRoute.path);
            return;
        }

        navigate(-1);
    }, [navigate, backLinks.archiveRoute.path, navigationType]);

    const handleClose = useCallback(
        () => navigateToArchive(),
        [navigateToArchive],
    );

    const currentRoute = useMemo(() => {
        const route = ROUTES_WITH_TOOLTIP.map((routeKey) => ({
            key: routeKey,
            ...getRoute(routeKey),
        })).find(
            (route) =>
                backLinks.label === route.label && pathname === route.path,
        );
        return route || null;
    }, [pathname, backLinks.label]);

    return (
        <div className={styles.breadcrumbsWrapper}>
            {pathname !== backLinks.archiveRoute.path && (
                <Button
                    clickHandler={handleBack}
                    className="flc"
                    isCustom
                    dataTest="back_link_prev_button"
                >
                    <IcoBackChevron className={styles.icon} />
                </Button>
            )}

            <div className={`flc ${styles.label}`} ref={containerRef}>
                <span ref={textRef}>{backLinks.label}</span>
                {currentRoute?.tooltip && (
                    <Tooltip
                        content={currentRoute.tooltip}
                        containerRef={containerRef}
                        textRef={textRef}
                        routeKey={currentRoute?.key}
                        alignment="center"
                        extraTopShift={-5}
                    />
                )}
            </div>

            <Button
                clickHandler={handleClose}
                className="flc"
                dataTest="back_link_close_button"
                isCustom
            >
                <IcoClose className={styles.icon} />
            </Button>
        </div>
    );
};

export default Breadcrumbs;
